import { Outlet, createFileRoute, useLocation, redirect } from "@tanstack/react-router";
import queryClient from "api/queryClient.ts";
import tenantAxiosInstance from "api/tenantAxiosInstance.ts";
import { getCurrencyQueryOptions } from "api/TenantSlug/Currency/queryOptions.ts";
import { getSettingsQueryOptions } from "api/TenantSlug/Setting/queryOptions.ts";
import { metaAtom } from "atoms/metaAtom";
import { tenantAtom } from "atoms/tenantAtom.ts";
import { tenantLocationAtom } from "atoms/tenantLocationAtom.ts";
import { TenantLayout } from "components/Layouts/TenantLayout/TenantLayout.tsx";
import { getSessionFromStorage, setSessionInStorage, useSession } from "hooks/useSession.ts";
import { useUser } from "hooks/useUser.ts";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { isUUID } from "utils/isUUID";

import CurrencyData = App.Data.CurrencyData;

export const Route = createFileRoute("/_authenticated/$tenantSlug/_layout")({
    component: TenantLayoutComponent,
    loader: async ({ params, context: { t } }) => {
        const { tenantSlug } = params as { tenantSlug?: string };

        let slugName = tenantSlug;
        const { user } = getSessionFromStorage();

        if (!slugName) {
            slugName = user?.tenants?.[0].slug_name || "";

            throw redirect({
                to: "/$tenantSlug",
                params: { tenantSlug: slugName },
            });
        }

        // user.tenants includes tenants the user has left (inactive pivot) — user_active reflects
        // the actual membership state, unlike mere presence in the array or the tenant's own active flag.
        const tenant =
            user?.tenants?.find((tenant) => tenant.slug_name === slugName && tenant.user_active !== false) ?? null;

        if (!tenant) {
            throw redirect({
                to: "/$tenantSlug/no-access",
                params: { tenantSlug: slugName },
                replace: true,
            });
        }

        tenantAxiosInstance.setTenantSlugInUrl(tenant.slug_name);

        // Clear all cached queries when switching tenants to prevent stale cross-tenant data
        const session = getSessionFromStorage();
        if (session.activeTenantSlug && session.activeTenantSlug !== tenant.slug_name) {
            queryClient.removeQueries();
        }
        setSessionInStorage({ token: session.token, user: session.user, activeTenantSlug: tenant.slug_name });

        let currency: CurrencyData | null = null;
        try {
            const { settings } = await queryClient.ensureQueryData(getSettingsQueryOptions());

            // TODO - na BE bude pak pridane aby bylo mozne si ziskavat na FE settings pres settingsData.settings.currency
            const currency_id = settings.find(({ key }) => key === "currency")?.value || "";

            const currencyData = await queryClient.ensureQueryData(getCurrencyQueryOptions(currency_id));
            currency = currencyData.currency;
        } catch {
            // noop
        }

        return {
            tenant,
            currency,
            user: user!, // Guaranteed by _authenticated loader
            breadcrumb: t("common.home"),
            tenantSlug: slugName,
        };
    },
});

function TenantLayoutComponent() {
    const sessionUtils = useSession();
    const setTenantLocationState = useSetRecoilState(tenantLocationAtom);
    const metaState = useRecoilValue(metaAtom);
    const location = useLocation();
    const userUtils = useUser();
    const setTenantData = useSetRecoilState(tenantAtom);

    const { tenantSlug, user, tenant, currency } = Route.useLoaderData();

    const [isInitialized, setInitialized] = useState(false);
    const activeTenant = userUtils.getActiveTenant();

    useEffect(() => {
        if (currency) {
            setTenantData((prev) => ({ ...prev, currency }));
        }
    }, [currency]);

    useEffect(() => {
        userUtils.setTenant(tenant);
        sessionUtils.setUser(user);

        setInitialized(true);
    }, [tenant.id, user.id]);

    useEffect(() => {
        const tenantLocation = location.pathname.replace(`/${tenantSlug}/`, "").replace(`/${tenantSlug}`, "");
        const paths = tenantLocation.split("/");
        const pathsWithoutUuids = paths.filter((segment) => !isUUID(segment)).filter((segment) => !isID(segment));
        setTenantLocationState({
            paths: paths,
            pathsWithoutUuids: pathsWithoutUuids,
            full: tenantLocation,
            fullWithSlug: location.pathname,
            locationTag: pathsWithoutUuids.join("."),
        });
    }, [location.pathname, tenantSlug]);

    // Wait until global state is fully populated (tenant + session atoms propagated)
    if (!isInitialized || !activeTenant) {
        return null;
    }

    return (
        <>
            <Helmet title={metaState.title} />
            <TenantLayout>
                <Outlet />
            </TenantLayout>
        </>
    );
}

const isID = (segment: string) => /^[0-9]+$/.test(segment);
